import { leerLocal, escribirLocal } from '../almacenamientoSeguro';
import { buildDelBinario } from './buildNativo';
import { esApp, plataforma } from './plataforma';

/** Build nativo mínimo por plataforma, tal como lo devuelve `GET /options/get/app-min-version`. */
export type VersionMinima = { ios: number; android: number };

/** La ficha de Arca en cada tienda, a donde manda el botón «Actualizar». */
export const URL_TIENDA: Record<keyof VersionMinima, string> = {
	ios: 'https://apps.apple.com/app/id6819609000',
	android: 'https://play.google.com/store/apps/details?id=app.arcadigital.app'
};

/**
 * Lo que se espera a la API antes de dar la comprobación por fallida (y dejar entrar). Al abrir, la
 * app no se pinta hasta saberlo (`ActualizacionObligatoria`): es lo máximo que puede tardar en arrancar.
 */
export const ESPERA_MAXIMA_MS = 3000;

/** Donde se guarda el último mínimo que dio la API, para cuando no responda. */
export const CLAVE_ULTIMO_MINIMO = 'arca.versionMinima.ultima';

/** Lo que dura una comprobación compartida (ver {@link comprobarVersion}). */
export const VENTANA_COMPARTIDA_MS = 5000;

const esNumero = (valor: unknown): valor is number =>
	typeof valor === 'number' && Number.isFinite(valor);

const esVersionMinima = (valor: unknown): valor is VersionMinima =>
	esNumero((valor as VersionMinima | null)?.ios) &&
	esNumero((valor as VersionMinima | null)?.android);

const ultimoMinimoGuardado = (): VersionMinima | null => {
	try {
		const guardado: unknown = JSON.parse(leerLocal(CLAVE_ULTIMO_MINIMO) ?? 'null');
		return esVersionMinima(guardado) ? guardado : null;
	} catch {
		return null;
	}
};

/**
 * El mínimo de la API y, si no responde, el último que dio. Sin esto, cerrar la app y reabrirla en
 * modo avión se saltaba el bloqueo. Un mínimo bajado (marcha atrás) llega con la siguiente respuesta.
 */
const minimoConocido = async (urlDeLaApi: string | undefined): Promise<VersionMinima | null> => {
	const minimo = await pedirVersionMinima(urlDeLaApi);
	if (minimo === null) {
		return ultimoMinimoGuardado();
	}

	escribirLocal(CLAVE_ULTIMO_MINIMO, JSON.stringify(minimo));
	return minimo;
};

/**
 * El build mínimo que exige el servidor. El endpoint es público: funciona antes del login.
 *
 * Devuelve `null` ante CUALQUIER fallo —sin red, más de {@link ESPERA_MAXIMA_MS} ms, error HTTP,
 * respuesta rara—. comparsa responde 200 a casi todo y mete el error dentro, así que no basta con
 * `ok`: se valida la forma.
 */
export const pedirVersionMinima = async (
	urlDeLaApi: string | undefined = process.env.REACT_APP_BASE_API
): Promise<VersionMinima | null> => {
	if (!urlDeLaApi) {
		return null;
	}

	const cancelar = new AbortController();
	const reloj = setTimeout(() => cancelar.abort(), ESPERA_MAXIMA_MS);

	try {
		const respuesta = await fetch(`${urlDeLaApi}/options/get/app-min-version`, {
			method: 'GET',
			signal: cancelar.signal
		});
		if (!respuesta.ok) {
			return null;
		}

		const cuerpo = await respuesta.json();
		const minimo: unknown = cuerpo?.data;
		if (!esVersionMinima(minimo)) {
			return null;
		}

		return { ios: minimo.ios, android: minimo.android };
	} catch {
		return null;
	} finally {
		clearTimeout(reloj);
	}
};

/**
 * ¿Este binario está por debajo del mínimo de su plataforma?
 *
 * - `true`: hay que actualizar. `false`: no (o es la web).
 * - `null`: **no se sabe** (sin build, sin respuesta válida y sin mínimo guardado). Quien llama decide;
 *   al abrir la app es FAIL-OPEN: bloquear a todo el mundo por un fallo nuestro es peor que dejar entrar
 *   a una versión vieja.
 *
 * El build y el mínimo se piden a la vez: la espera total es la de la API ({@link ESPERA_MAXIMA_MS}).
 */
export const hayQueActualizar = async (
	urlDeLaApi: string | undefined = process.env.REACT_APP_BASE_API
): Promise<boolean | null> => {
	if (!esApp()) {
		return false;
	}

	const [build, minimo] = await Promise.all([buildDelBinario(), minimoConocido(urlDeLaApi)]);
	if (build === null || minimo === null) {
		return null;
	}

	return build < minimo[plataforma() as keyof VersionMinima];
};

let compartida: { promesa: Promise<boolean | null>; desde: number } | null = null;

/**
 * {@link hayQueActualizar}, compartida durante {@link VENTANA_COMPARTIDA_MS}: la pantalla de actualizar
 * y el bloqueo biométrico se enteran a la vez de que la app vuelve a primer plano, y los dos tienen que
 * esperar a la MISMA respuesta (una sola llamada) para que Face ID no pida la cara si toca actualizar.
 */
export const comprobarVersion = (
	urlDeLaApi: string | undefined = process.env.REACT_APP_BASE_API
): Promise<boolean | null> => {
	if (compartida === null || Date.now() - compartida.desde >= VENTANA_COMPARTIDA_MS) {
		compartida = { promesa: hayQueActualizar(urlDeLaApi), desde: Date.now() };
	}
	return compartida.promesa;
};

/** Solo para los tests: empezar sin comprobación compartida. */
export const olvidarComprobacion = (): void => {
	compartida = null;
};
